import { SphereDivider } from "@components/common/sphere-divider";
import { ExternalLinks, LinkName } from "@components/external-links";
import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { Box } from "@mui/material";
import { CoreAPITypes } from "@stellar/api-logic";
import { formatUserRoleType } from "@utils/data-display";
import { FaroSelect } from "@components/common/faro-select/faro-select";
import { LabelWithHelp } from "@components/common/label-with-help";
import { SelectRoleSubjectProps } from "@components/role/select-role-utils";
import { sphereColors } from "@styles/common-colors";
import {
  getRoleMenuItemSx,
  RoleMenuItem,
} from "@components/role/role-menu-item";
import { DataTestIdProps } from "@custom-types/ui-types";

interface Props<T> extends DataTestIdProps, SelectRoleSubjectProps<T> {
  /** All the roles that are selectable */
  roles: T[];

  /** The label to show above the select dropdown */
  label: string;

  /** if passed it will show the link to the related roles help page */
  link?: LinkName;
}

/** Shows a selector in which user can choose a role for different subjects, i.e company, project, group */
export function SelectRole<
  T extends CoreAPITypes.EUserCompanyRole | CoreAPITypes.EUserProjectRole
>({
  roles,
  disabledRoles = [],
  isTableCell,
  selectedRole,
  onChange,
  onResetSelection,
  label,
  link,
  placeholder,
  isRequired = true,
  dataTestId,
}: Props<T>): JSX.Element {
  return (
    <>
      {!isTableCell && <LabelWithHelp title={label} isRequired={isRequired} />}
      <FaroSelect
        labelId="select-role-label"
        id="select-role"
        dataTestId={dataTestId}
        size="small"
        variant={isTableCell ? "tableCell" : "outlined"}
        value={selectedRole ?? ""}
        onChange={onChange}
        renderValue={
          selectedRole ? () => formatUserRoleType(selectedRole) : undefined
        }
        placeholder={placeholder}
        onResetSelection={onResetSelection}
      >
        {roles.map((role) => (
          <FaroMenuItem
            key={role}
            value={role}
            disabled={disabledRoles.includes(role)}
            sx={getRoleMenuItemSx({ isTableCell })}
          >
            <RoleMenuItem role={role} isRoleSelected={role === selectedRole} />
          </FaroMenuItem>
        ))}

        {link && (
          <Box sx={{ mb: "8px" }}>
            <SphereDivider sx={{ m: "8px" }} />
            <ExternalLinks
              link={link}
              text="Learn about permissions levels"
              sx={{
                fontSize: "12px",
                px: "16px",
                mx: "8px",
                color: sphereColors.blue500,
              }}
            />
          </Box>
        )}
      </FaroSelect>
    </>
  );
}
